import { useQuery } from '@tanstack/react-query'
import { fetchRegisters, fetchStandingAlerts } from '../api/attendance'
import { fetchReportedAbsences, newSince } from '../api/reportedAbsences'
import { fetchConflicts } from '../api/bookings'
import { addDays, startOfWeek, today } from '../api/calendar'
import { fetchUnreadConversations, UNREAD_KEY } from '../api/conversations'
import { useCan, useSession } from '../useSession'

/**
 * What is waiting, per destination (D-322, D-324).
 *
 * **A rail badge answers *where*.** It carries only what belongs to one
 * destination — the bell carries what has no destination of its own, and Home
 * orders the same facts by cost. Three surfaces, three questions.
 *
 * **This is where D-167's summing stops.** That decision added two badge
 * sources into one number, which was right for a closed menu holding two kinds
 * of pending work and is wrong once each has somewhere of its own to sit: a
 * number nobody can attribute is one people learn to ignore. So conflicts badge
 * Calendar, unfinished registers badge Attendance, and **pending booking
 * approvals badge neither** — a request awaiting a decision is exactly what the
 * bell is for, and it arrives there in M3 rather than being folded in here.
 *
 * Each query is gated on the capability that makes its screen reachable, so an
 * account that cannot open a destination never fetches its count.
 */
/**
 * The same counts, keyed by the view they belong beside.
 *
 * A destination badge says *something in here needs you*; a view count says
 * *which of these*. Same numbers, two questions - which is why they come from
 * one place rather than each screen fetching its own and drifting.
 */
export function useViewCounts(): Record<string, number> {
  const { unmarked, unseen } = useAttendanceCounts()
  const standing = useStandingCount()

  return {
    // Who is under the line, or has a class under it (D-590).
    '/attendance': standing,
    // Missing moved to its own address when Standing took the first tab (D-590).
    '/attendance/missing': unmarked,
    '/attendance/absences': unseen,
    '/my-standing': standing,
    '/my-attendance': unmarked,
    '/absences': unseen,
  }
}

/**
 * Clashes in the current week, for whichever surface is asking.
 *
 * <b>One definition, however many things ask it</b> (D-578). It was three - the
 * rail badge, the chip that shouted off the calendar, and the Conflicts tab -
 * and D-585 and D-593 retired two of them, so today the rail badge is the only
 * caller. It stays a shared definition all the same: the risk was never a
 * duplicate request (the key dedupes those) but a duplicate *date range* -
 * this asks about **this week**, and a copy that quietly asked a fortnight
 * would put two different numbers on one screen with nothing to say which
 * was right.
 *
 * Gated on the capability that makes the screen reachable, so an account that
 * cannot open it never fetches.
 */
export function useConflictCount(): number {
  const canViewAllSchedules = useCan('ViewAllSchedules')

  const { data } = useQuery({
    queryKey: ['conflicts', 'nav'],
    queryFn: () => fetchConflicts(startOfWeek(today()), addDays(startOfWeek(today()), 6)),
    staleTime: 60_000,
    enabled: canViewAllSchedules,
  })

  return data?.length ?? 0
}

/**
 * Conversations with something unread in them (D-638).
 *
 * <b>Gated on the module as well as the capability</b>, because `Messaging`
 * arrives switched off (D-625) and every messaging route 404s on a host that
 * has not enabled it. Without the module check, every signed-in page on
 * production would ask a question it knows the answer to and get a 404 for it.
 */
function useUnreadConversations(): number {
  const canSendMessages = useCan('SendMessages')
  const { session } = useSession()

  const { data } = useQuery({
    queryKey: UNREAD_KEY,
    queryFn: fetchUnreadConversations,
    staleTime: 60_000,
    enabled: canSendMessages && session?.modules?.messaging === true,
  })

  return data?.conversations ?? 0
}

/**
 * How many dancers on the Standing view need attention (D-590, D-765), for the
 * tab beside it - the studio's under Attendance, a teacher's under her Classes.
 *
 * Gated on `ViewAttendance`, which is what opens either door; the server
 * scopes a teacher to her own classes, so the count is hers as well.
 *
 * **`needsAttention`, not the length of the list.** The two were the same
 * number while the view listed only the dancers in trouble; the day it started
 * listing everybody (D-765) a badge reading the length would have gone from
 * *1* to *113* with nothing failing, and a badge that is always lit is one
 * nobody reads. Falls back to the length for a server that has not been
 * deployed yet, which is what a browser holding an old bundle sees for the ~75
 * seconds a deploy takes (D-33).
 */
function useStandingCount(): number {
  const canViewAttendance = useCan('ViewAttendance')

  const { data } = useQuery({
    queryKey: ['standing-alerts', 'nav'],
    queryFn: fetchStandingAlerts,
    staleTime: 60_000,
    enabled: canViewAttendance,
  })

  return data?.needsAttention ?? data?.alerts.length ?? 0
}

/**
 * The two attendance numbers, fetched once for whoever asks.
 *
 * **Gated on `TakeAttendance` and `ViewAttendance`, not `ManageAttendance`.**
 * The destination is the admin one and is gated the narrower way (D-322), but
 * these same numbers now sit beside a teacher's own views under My classes -
 * and she is the person the count was written for (D-188). Narrowing them to the
 * destination would have taken her badge away with the header menu.
 */
function useAttendanceCounts(): { unmarked: number; unseen: number } {
  const canTakeAttendance = useCan('TakeAttendance')
  const canViewAttendance = useCan('ViewAttendance')

  const { data: unmarked } = useQuery({
    queryKey: ['registers', 'nav'],
    queryFn: () => fetchRegisters(true),
    staleTime: 60_000,
    enabled: canTakeAttendance,
  })

  const { data: reportedOut } = useQuery({
    queryKey: ['reported-absences', 'nav'],
    queryFn: () => fetchReportedAbsences(),
    staleTime: 60_000,
    enabled: canViewAttendance,
  })

  return {
    unmarked: unmarked?.length ?? 0,
    unseen: reportedOut ? newSince(reportedOut.absences, reportedOut.seenAt).length : 0,
  }
}

/**
 * What each rail badge is counting, named (D-324).
 *
 * **The no-double-count rule needs both halves written down to be checkable.**
 * D-324 reversed D-167's summing so that three surfaces could answer three
 * questions - a rail badge says *where*, the bell says *what just happened*,
 * Home says *what should I do now* - and the rule that keeps them honest is
 * that nothing is counted on two of them.
 *
 * There is exactly one overlap now, `Conversation` (D-638), and it is declared
 * in `SHARED_WITH_BELL` with the argument for it. Everything else here counts
 * something that is not a notification at all. So the lists stay data and a
 * test intersects them - the day somebody badges a destination with a
 * notification subject, it goes red until they either drop the badge or write
 * down why both surfaces should carry it.
 *
 * **A second test binds these keys to the ones `useNavBadges` actually
 * returns**, and that is what makes the first one reachable. Without it this
 * object had exactly one consumer - the test reading it - and its strings
 * appeared nowhere else in the repository, so a future implementer adding a
 * badge would never come within sight of it. A declaration nothing checks is a
 * comment with a type annotation.
 */
export const BADGE_COUNTS: Record<string, readonly string[]> = {
  conflicts: ['Conflict'],
  attendance: ['UnmarkedRegister', 'ReportedAbsence'],
  classes: ['UnmarkedRegister', 'ReportedAbsence'],
  messages: ['Conversation'],
}

/**
 * The notification subject types the bell carries.
 *
 * Mirrors `NotificationSubject`'s factory methods in the domain. A new kind is
 * a template row and a subject type (D-285); adding it here is what asserts it
 * did not quietly become a second copy of something the rail already counts.
 */
export const BELL_SUBJECTS = [
  'PrivateRequest',
  'Dancer',
  'Cover',
  // Three the mirror had fallen behind on (found writing D-543): the domain had
  // six factories and this listed three, so the no-double-count test was
  // checking half the bell against the rail.
  'Class',
  'Booking',
  'MakeupRequest',
  // The envelope's rows (D-543, D-547): the bell counts them like any other
  // notification, and the Requests list counts asks — never both on the badge.
  'Ask',
  // A conversation with something unread in it (D-623). The bell carries it
  // and **so does the rail** (D-638) - the one subject deliberately on both,
  // declared below so the intersection test can let it past and stop there.
  'Conversation',
] as const

/**
 * The subjects a rail badge may also count, and why each one is allowed.
 *
 * <b>An allowance, rather than a hole in the rule.</b> The no-double-count test
 * fails for any subject on both lists that is not named here, and a second test
 * fails for a name here that no badge counts - so an entry cannot be added
 * without a badge, and cannot outlive one.
 *
 * <b>What it does not do is force anybody to write the reason.</b> The value is
 * the badge key, because that is what the second test can check; the argument
 * beside each entry is a comment, and a one-line addition with no comment would
 * compile and pass. That is a real limit of this guard and is written down
 * rather than claimed away: what it makes mechanical is *declaring* the
 * overlap, which is the step that puts somebody in front of this paragraph.
 */
export const SHARED_WITH_BELL: Partial<Record<(typeof BELL_SUBJECTS)[number], string>> = {
  // D-638. The bell totals every subject, so its number cannot say *messages*;
  // the rail badge is the attribution, counting the same conversations the
  // same way so the two can never disagree.
  Conversation: 'messages',
}

export function useNavBadges(): Record<string, number> {
  const { unmarked, unseen } = useAttendanceCounts()
  const conflicts = useConflictCount()
  const messages = useUnreadConversations()

  /*
   * Attendance is one destination holding two views, so its badge is one
   * number - and that is not the summing D-324 undid. **D-167 added two
   * unrelated subjects onto a container that held both**; this adds two moments
   * of the same subject, before a class and after it (D-188), onto the
   * destination that is about exactly that. Tapping it lands where both are.
   */
  return {
    /*
     * <b>On Conflicts, not on Calendar</b> (D-585, amending D-578). The count
     * sat on the calendar while a single view owned all of it, so the rail and
     * the tab said seventeen twice - and the badge implied the calendar needed
     * attention rather than the clashes inside it. D-578 put the number on the
     * tab; there is no tab now, and this is where it belongs.
     */
    conflicts,
    attendance: unmarked + unseen,
    classes: unmarked + unseen,
    /*
     * <b>Messages badges, and the bell keeps carrying it too</b> (D-638,
     * amending D-623 and D-593's application of D-324).
     *
     * The no-double-count rule below was right about Requests and wrong here,
     * and the difference is what the bell's number *means*. It totals seven
     * subject types, so a reader holding two unread conversations and three
     * open asks sees five - and five is exactly the unattributable number
     * D-324 exists to prevent. The rule was written to stop one fact being
     * summed into a badge nobody can read; on Messages it was stopping the
     * only surface that could attribute it.
     *
     * So this counts the same fact the bell counts, the same way - one per
     * conversation with something unread (D-623) - which is what keeps the two
     * from ever disagreeing. `SHARED_WITH_BELL` is how the rule knows
     * this was chosen rather than forgotten.
     */
    messages,
  }
}
